import { useState, useEffect, useCallback, MouseEventHandler } from 'react';
import { NO_GROUP_ID } from '@api/Groups.api';
import { useStore } from '@ui/hooks/useStore';
import getLiElementId from '@utils/getLiElementId';
import { IState } from './IState';

const useSubusers = () => {
    const { subusers, map, app, groups, devices, lang } = useStore();

    const initState: IState = {
        addSubuserVisible: false,
        addDeviceToSubuserVisible: false,
        editSubuserVisible: false,
        deleteSubuserVisible: false,
        subuserDevicesVisible: false,
        firstLoading: true,
        editSubuserRightsVisible: false,
        deleteDeviceVisible: false,
        deviceBtnHover: false,
        visibleList: {},
    };
    const [state, setState] = useState(initState);

    const handleSubuserClick: MouseEventHandler<HTMLElement> = useCallback(
        (e) => {
            const login = getLiElementId(e.target as HTMLElement);
            if (login)
                setState((prev) => {
                    const visibleList = { ...prev.visibleList };
                    visibleList[login] = !visibleList[login];
                    return { ...prev, visibleList };
                });
        },
        [],
    );
    const handleAddSubuserOpen = useCallback(() => {
        setState((prev) => ({ ...prev, addSubuserVisible: true }));
    }, []);
    const handleAddSubuserClose = useCallback(() => {
        setState((prev) => ({ ...prev, addSubuserVisible: false }));
    }, []);
    const handleAddDeviceToSubuserOpen: MouseEventHandler<HTMLElement> =
        useCallback((e) => {
            e.preventDefault();
            e.stopPropagation();
            const login = getLiElementId(e.target as HTMLElement);
            if (login) {
                subusers.setSelectedSubuser(login);
                setState((prev) => ({
                    ...prev,
                    addDeviceToSubuserVisible: true,
                }));
            }
        }, []);
    const handleAddDeviceToSubuserClose = useCallback(() => {
        setState((prev) => ({ ...prev, addDeviceToSubuserVisible: false }));
        subusers.resetSelectedSubuser();
    }, []);
    const handleEditSubuserOpen: MouseEventHandler<HTMLElement> = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            const login = getLiElementId(e.target as HTMLElement);
            if (login) {
                subusers.setSelectedSubuser(login);
                setState((prev) => ({ ...prev, editSubuserVisible: true }));
            }
        },
        [],
    );
    const handleEditSubuserClose = useCallback(() => {
        setState((prev) => ({ ...prev, editSubuserVisible: false }));
        subusers.resetSelectedSubuser();
    }, []);
    const handleDeleteSubuserOpen: MouseEventHandler<HTMLElement> = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            const login = getLiElementId(e.target as HTMLElement);
            if (login) {
                subusers.setSelectedSubuser(login);
                setState((prev) => ({ ...prev, deleteSubuserVisible: true }));
            }
        },
        [],
    );
    const handleDeleteSubuserClose = useCallback(() => {
        setState((prev) => ({ ...prev, deleteSubuserVisible: false }));
        subusers.resetSelectedSubuser();
    }, []);
    const handleSubuserDevicesClose = useCallback(() => {
        setState((prev) => ({ ...prev, subuserDevicesVisible: false }));
        subusers.resetSelectedSubuser();
    }, []);
    const handleEditSubuserRightsOpen: MouseEventHandler<HTMLElement> =
        useCallback((e) => {
            e.preventDefault();
            e.stopPropagation();
            const id = getLiElementId(e.target as HTMLElement);
            if (!id) return;
            const [imei, login] = id.split('&');
            if (imei && login) {
                subusers.setSelectedSubuser(login);
                subusers.setSelectedDevice(imei);
                setState((prev) => ({
                    ...prev,
                    editSubuserRightsVisible: true,
                }));
            }
        }, []);
    const handleEditSubuserRightsClose = useCallback(() => {
        setState((prev) => ({ ...prev, editSubuserRightsVisible: false }));
        subusers.resetSelectedDevice();
        subusers.resetSelectedSubuser();
    }, []);
    const handleDeviceBtnHoverStart = useCallback(() => {
        setState((prev) => ({ ...prev, deviceBtnHover: true }));
    }, []);
    const handleDeviceBtnHoverStop = useCallback(() => {
        setState((prev) => ({ ...prev, deviceBtnHover: false }));
    }, []);
    const handleDeleteDeviceOpen: MouseEventHandler<HTMLElement> = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();
            const id = getLiElementId(e.target as HTMLElement);
            if (!id) return;
            const [imei, login] = id.split('&');
            if (imei && login) {
                subusers.setSelectedSubuser(login);
                subusers.setSelectedDevice(imei);
                setState((prev) => ({ ...prev, deleteDeviceVisible: true }));
            }
        },
        [],
    );
    const handleDeleteDeviceClose = useCallback(() => {
        setState((prev) => ({ ...prev, deleteDeviceVisible: false }));
        subusers.resetSelectedDevice();
        subusers.resetSelectedSubuser();
    }, []);
    const handlers = {
        handleAddSubuserOpen,
        handleAddSubuserClose,
        handleEditSubuserOpen,
        handleEditSubuserClose,
        handleDeleteSubuserOpen,
        handleDeleteSubuserClose,
        handleSubuserDevicesClose,
        handleAddDeviceToSubuserOpen,
        handleAddDeviceToSubuserClose,
        handleEditSubuserRightsOpen,
        handleEditSubuserRightsClose,
        handleDeviceBtnHoverStart,
        handleDeviceBtnHoverStop,
        handleDeleteDeviceOpen,
        handleDeleteDeviceClose,
        handleSubuserClick,
    };

    useEffect(() => {
        subusers.getSubusers();
    }, []);
    useEffect(() => {
        const groupsLen = groups.groupsList.length;
        const devicesLen = devices.deviceList.length;
        if (
            app.page === 'subusers' &&
            state.firstLoading &&
            groupsLen > 0 &&
            devicesLen > 0 &&
            !map.selectedGroupId
        ) {
            let id = localStorage.getItem('groupId');
            if (!id) {
                id = NO_GROUP_ID;
            }
            const listLen = devices.deviceList
                .filter((device) => device.groupId === id)
                .map(({ imei }) => imei).length;
            if (listLen > 0) {
                map.setSelectedGroupId(id);
                setState((prev) => ({ ...prev, firstLoading: false }));
            }
        }
    }, [groups.groupsList, devices.deviceList, app.page]);

    return {
        subusers,
        groups,
        devices,
        lang,
        state,
        handlers,
    };
};

export default useSubusers;
